import { Fragment } from "react";
import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { Transition } from "@headlessui/react";
import { createPortal } from "react-dom";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useBuilderStore } from "@circle-react-shared/BuilderUI";

interface TransitionProps {
  afterEnter?: () => void;
  afterLeave?: () => void;
  appear?: boolean;
  beforeEnter?: () => void;
  beforeLeave?: () => void;
  enter?: string;
  entered?: string;
  enterFrom?: string;
  enterTo?: string;
  leave?: string;
  leaveFrom?: string;
  leaveTo?: string;
  show?: boolean;
}

export interface ExpandedSidebarProps extends ComponentPropsWithoutRef<"div"> {
  children?: ReactNode;
  className?: string;
  portalTo?: Element;
  transitionProps?: TransitionProps;
}

export const ExpandableActionSidebar = ({
  children,
  className,
  transitionProps,
  portalTo,
  ...rest
}: ExpandedSidebarProps) => {
  const isActionSidebarOpen = useBuilderStore(
    state => state.isActionSidebarOpen,
  );

  const drawer = (
    <Transition
      as={Fragment}
      show={isActionSidebarOpen}
      enter="transition-transform ease-out duration-300"
      enterFrom="-translate-x-full"
      enterTo="translate-x-0"
      leave="transition-transform ease-in duration-200"
      leaveFrom="translate-x-0"
      leaveTo="-translate-x-full"
      unmount={false}
      {...transitionProps}
    >
      <div
        className={classNames(
          "bg-primary absolute left-16 top-0 z-[-10] flex h-full flex-col shadow-[4px_0_10px_-3px_rgb(0,0,0,0.1)]",
          className,
        )}
        {...rest}
      >
        {children}
      </div>
    </Transition>
  );

  if (!portalTo) {
    return drawer;
  }

  return createPortal(drawer, portalTo);
};
